import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Alert,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  HelpOutline as HelpIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';
import CargaSeguradorasDialog from './CargaSeguradorasDialog';

const TOOLTIP_REGISTRO_UNITARIO = `Determina como o sistema trata cada linha da planilha desta seguradora.

UNITÁRIO: Cada linha da planilha é 1 seguro individual. O sistema grava Quantidade=1 automaticamente para cada registro. Exemplo: Novo Seguros, Ituran, Suhai, Ezze.

AGREGADO: Cada linha representa o total de uma corretora (pode conter vários seguros). O sistema usa a coluna Quantidade da planilha se existir. Exemplo: HDI, Tokio, Darwin, Justos.`;

const TOOLTIP_TIPO_META = `Define como a meta do comercial é comparada no Power BI para esta seguradora.

VALOR (R$): O Power BI compara o total de prêmios importados (em reais) com a meta de valor cadastrada. Usado para seguradoras tradicionais.

QUANTIDADE: O Power BI compara o número de registros com Tipo Movimento = 'Nova Cotação' com a meta de quantidade cadastrada. Usado para seguradoras como Darwin, Justos, Ituran e Novo Seguros.`;

const SeguradoorasPanel = () => {
  const [seguradoras, setSeguradoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSeguradora, setEditingSeguradora] = useState(null);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    identificador_interno: '',
    registro_unitario: true,
    tipo_meta_padrao: 'valor',
    comercial_por: 'corretora'
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('nome');
  const [order, setOrder] = useState('asc');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [openCargaDialog, setOpenCargaDialog] = useState(false);

  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return !!u.is_admin;
    } catch {
      return false;
    }
  })();

  const fetchSeguradoras = async () => {
    try {
      setLoading(true);
      const response = await api.get('/seguradoras');
      const raw = response.data;
      const lista = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.results)
            ? raw.results
            : [];
      setSeguradoras(lista);
    } catch (err) {
      setSeguradoras([]);
      setError('Erro ao carregar seguradoras. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    fetchSeguradoras();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData(prev => ({ ...prev, registro_unitario: e.target.checked }));
  };

  const resetForm = () => {
    setFormData({ nome: '', identificador_interno: '', registro_unitario: true, tipo_meta_padrao: 'valor', comercial_por: 'corretora' });
    setEditingSeguradora(null);
  };

  const handleOpenDialog = (seguradora = null) => {
    if (seguradora) {
      setEditingSeguradora(seguradora);
      setFormData({
        nome: seguradora.nome || '',
        identificador_interno: seguradora.identificador_interno || '',
        registro_unitario: seguradora.registro_unitario !== false,
        tipo_meta_padrao: seguradora.tipo_meta_padrao || 'valor',
        comercial_por: seguradora.comercial_por || 'corretora'
      });
    } else {
      resetForm();
    }
    setFormError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
    setError('');
    setFormError('');
  };

  const handleSave = async () => {
    try {
      setFormError('');
      if (!formData.nome.trim()) {
        setFormError('Nome da seguradora é obrigatório');
        return;
      }
      if (!formData.identificador_interno.trim()) {
        setFormError('Identificador interno é obrigatório');
        return;
      }

      const payload = {
        nome: formData.nome.trim(),
        identificador_interno: formData.identificador_interno.trim().toLowerCase(),
        registro_unitario: formData.registro_unitario,
        tipo_meta_padrao: formData.tipo_meta_padrao,
        comercial_por: formData.comercial_por
      };

      if (editingSeguradora) {
        const res = await api.put(`/seguradoras/${editingSeguradora.id}`, payload);
        const bi = res.data?.data?.resultados_bi_atualizados;
        setSuccess(`Seguradora atualizada com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      } else {
        await api.post('/seguradoras', payload);
        setSuccess('Seguradora criada com sucesso!');
      }

      handleCloseDialog();
      fetchSeguradoras();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Erro ao salvar seguradora');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await api.delete(`/seguradoras/${confirmDeleteId}`);
      setSuccess('Seguradora excluída com sucesso!');
      setConfirmDeleteId(null);
      fetchSeguradoras();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setConfirmDeleteId(null);
      setError(err.response?.data?.message || 'Erro ao excluir seguradora');
    }
  };

  const filteredSeguradoras = Array.isArray(seguradoras) ? seguradoras
    .filter(seg => {
      const term = searchTerm.trim().toLowerCase();
      const byId = seg.id !== undefined && String(seg.id).includes(searchTerm.trim());
      const byNome = seg.nome?.toLowerCase().includes(term);
      const byIdent = seg.identificador_interno?.toLowerCase().includes(term);
      return byId || byNome || byIdent;
    })
    .sort((a, b) => {
      if (orderBy === 'id') {
        const diff = (a.id || 0) - (b.id || 0);
        return order === 'asc' ? diff : -diff;
      }
      const aVal = a[orderBy]?.toString() || '';
      const bVal = b[orderBy]?.toString() || '';
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }) : [];

  const paginatedSeguradoras = filteredSeguradoras.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h5" gutterBottom>
            Gerenciar Seguradoras
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cadastre e gerencie as seguradoras do sistema
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pesquisar por ID, nome ou identificador"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchTerm && (
                      <IconButton onClick={() => setSearchTerm('')} size="small">
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {isAdmin && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setOpenCargaDialog(true)}
                  >
                    Carga em Massa
                  </Button>
                )}
                <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                  Nova Seguradora
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: 80, fontWeight: 'bold' }}>
                      <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleRequestSort('id')}>
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'nome'} direction={orderBy === 'nome' ? order : 'asc'} onClick={() => handleRequestSort('nome')}>
                        Nome
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'identificador_interno'} direction={orderBy === 'identificador_interno' ? order : 'asc'} onClick={() => handleRequestSort('identificador_interno')}>
                        Identificador Interno
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Registro</TableCell>
                    <TableCell align="center">Meta</TableCell>
                    <TableCell align="center">Comercial</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
                    </TableRow>
                  ) : paginatedSeguradoras.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">
                          {searchTerm ? 'Nenhuma seguradora encontrada' : 'Nenhuma seguradora cadastrada'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedSeguradoras.map((seguradora) => (
                      <TableRow key={seguradora.id} hover>
                        <TableCell><code>{seguradora.id}</code></TableCell>
                        <TableCell>{seguradora.nome}</TableCell>
                        <TableCell><code>{seguradora.identificador_interno}</code></TableCell>
                        <TableCell align="center">
                          {seguradora.registro_unitario !== false
                            ? <Chip label="Unitário" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                            : <Chip label="Agregado" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                          }
                        </TableCell>
                        <TableCell align="center">
                          {(seguradora.tipo_meta_padrao || 'valor') === 'quantidade'
                            ? <Chip label="Quantidade" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                            : <Chip label="Valor (R$)" size="small" sx={{ bgcolor: '#f5f5f5', color: '#424242', fontWeight: 600 }} />
                          }
                        </TableCell>
                        <TableCell align="center">
                          {(seguradora.comercial_por || 'corretora') === 'seguradora'
                            ? <Chip label="Seguradora" size="small" color="primary" variant="outlined" />
                            : <Chip label="Corretora" size="small" variant="outlined" />
                          }
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => handleOpenDialog(seguradora)} color="primary" size="small">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => setConfirmDeleteId(seguradora.id)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredSeguradoras.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
              labelRowsPerPage="Registros por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Paper>

          {/* Dialog criar/editar */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingSeguradora ? 'Editar Seguradora' : 'Nova Seguradora'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                {formError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
                    {formError}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nome da Seguradora"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      required
                      helperText="Nome completo da seguradora (ex: Tokio Marine)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Identificador Interno"
                      name="identificador_interno"
                      value={formData.identificador_interno}
                      onChange={handleInputChange}
                      required
                      helperText="Identificador usado no código (ex: tokio_marine, hdi, suhai)"
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                    />
                  </Grid>

                  {/* Registro Unitário */}
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ mr: 0.5 }}>Registro Unitário</Typography>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{TOOLTIP_REGISTRO_UNITARIO}</span>} arrow placement="right">
                          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.registro_unitario}
                            onChange={handleSwitchChange}
                            color="success"
                          />
                        }
                        label={formData.registro_unitario ? 'Unitário (cada linha = 1 seguro)' : 'Agregado (cada linha = total da corretora)'}
                      />
                      <FormHelperText>
                        Ativado = cada linha da planilha representa 1 seguro individual (ex: Novo Seguros, Ituran).
                        Desativado = cada linha representa o total agregado de uma corretora (ex: HDI, Darwin).
                      </FormHelperText>
                    </Box>
                  </Grid>

                  {/* Tipo de Meta */}
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ mr: 0.5 }}>Tipo de Meta</Typography>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{TOOLTIP_TIPO_META}</span>} arrow placement="right">
                          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Meta</InputLabel>
                        <Select
                          name="tipo_meta_padrao"
                          value={formData.tipo_meta_padrao}
                          onChange={handleInputChange}
                          label="Tipo de Meta"
                        >
                          <MenuItem value="valor">Valor (R$)</MenuItem>
                          <MenuItem value="quantidade">Quantidade</MenuItem>
                        </Select>
                        <FormHelperText>
                          Valor = meta acompanhada em reais. Quantidade = meta por número de seguros novos (ex: Darwin, Justos, Ituran, Novo Seguros).
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Comercial vinculado por */}
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Comercial vinculado por</Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Comercial por</InputLabel>
                        <Select
                          name="comercial_por"
                          value={formData.comercial_por}
                          onChange={handleInputChange}
                          label="Comercial por"
                        >
                          <MenuItem value="corretora">Corretora</MenuItem>
                          <MenuItem value="seguradora">Seguradora</MenuItem>
                        </Select>
                        <FormHelperText>
                          "Corretora": cada corretora tem seu comercial (padrão atual). "Seguradora": um único comercial atende toda a carteira desta seguradora.
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">Cancelar</Button>
              <Button onClick={handleSave} variant="contained">
                {editingSeguradora ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmação de exclusão */}
          <Dialog open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Excluir Seguradora</DialogTitle>
            <DialogContent>
              <Typography>
                Tem certeza que deseja excluir esta seguradora? Esta ação não pode ser desfeita.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteId(null)} color="inherit">Cancelar</Button>
              <Button onClick={handleConfirmDelete} color="error" variant="contained">Excluir</Button>
            </DialogActions>
          </Dialog>

          <CargaSeguradorasDialog
            open={openCargaDialog}
            onClose={() => setOpenCargaDialog(false)}
            onSuccess={fetchSeguradoras}
          />
        </Box>
      </Container>
    </Layout>
  );
};

export default SeguradoorasPanel;
